// Owner-side broadcasts hooks (slice 5). Backs the /owner/broadcasts page.
//
// Backend lives at /api/broadcasts (NOT /api/owner/broadcasts) — the existing
// surface from CLAUDE.md's "Push broadcasts" section. We read it as-is.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { BroadcastAudience, BroadcastInput } from '@naya/shared'

import { ownerApi } from '../ownerApi.js'
import { broadcastCapKey } from './useBroadcastCap.js'
import type {
  BroadcastCapabilitiesResponse,
  BroadcastDetailResponse,
  CancelBroadcastResponse,
  CreateBroadcastResponse,
  ListBroadcastsResponse,
  PreviewCountResponse,
  RemoveBroadcastResponse,
} from '../types.js'

export const broadcastsKey = ['owner', 'broadcasts'] as const
export const broadcastDetailKey = (id: string) =>
  ['owner', 'broadcasts', id] as const

export function useBroadcasts() {
  return useQuery<ListBroadcastsResponse>({
    queryKey: broadcastsKey,
    queryFn: () => ownerApi.get<ListBroadcastsResponse>('/broadcasts?limit=50'),
    // The worker ticks every 60s; an extra poll on focus keeps the UI
    // responsive after a publish without forcing manual refreshes.
    refetchOnWindowFocus: true,
    // While any row is in flight (pending/sending/removing), poll every 5s
    // so status pills + counters tick without the owner having to refresh.
    refetchInterval: (query) => {
      const rows = query.state.data?.broadcasts ?? []
      const anyInFlight = rows.some(
        (b) =>
          b.status === 'pending' ||
          b.status === 'sending' ||
          b.status === 'removing',
      )
      return anyInFlight ? 5000 : false
    },
  })
}

export function useBroadcastDetail(id: string | null) {
  return useQuery<BroadcastDetailResponse>({
    queryKey: id ? broadcastDetailKey(id) : ['owner', 'broadcasts', 'none'],
    queryFn: () =>
      ownerApi.get<BroadcastDetailResponse>(`/broadcasts/${id}`),
    enabled: id !== null,
    // Refetch every 5s while a broadcast is in flight so the recipients
    // counter ticks up live. The list-level invalidate on focus covers the
    // terminal-status transition.
    refetchInterval: (query) => {
      const status = query.state.data?.broadcast.status
      return status === 'pending' ||
        status === 'sending' ||
        status === 'removing'
        ? 5000
        : false
    },
  })
}

export function useCreateBroadcast() {
  const qc = useQueryClient()
  return useMutation<CreateBroadcastResponse, Error, BroadcastInput>({
    mutationFn: (input) =>
      ownerApi.post<CreateBroadcastResponse>('/broadcasts', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: broadcastsKey })
      // The cadence card ("N broadcasts in the last 7 days") reads a
      // separate query — invalidate it too or the count stays stale until
      // its 30s staleTime lapses.
      void qc.invalidateQueries({ queryKey: broadcastCapKey })
    },
  })
}

/**
 * On-demand recipient count for an audience. A mutation, not a query — the
 * composer fires it only when the owner clicks "Check audience", so the count
 * is never computed on every keystroke. The component caches the last result
 * and the audience it was for, and re-checks when the audience changes.
 */
export function usePreviewCount() {
  return useMutation<
    PreviewCountResponse,
    Error,
    { audience: BroadcastAudience; program_id?: string }
  >({
    mutationFn: (body) =>
      ownerApi.post<PreviewCountResponse>('/broadcasts/preview-count', body),
  })
}

/**
 * The tenant's broadcast capabilities — which curated segments it may use and
 * whether the custom filter builder is unlocked. Resolved from plan tier +
 * per-tenant override. Drives which audience options the composer offers.
 */
export const broadcastCapabilitiesKey = [
  'owner',
  'broadcast-capabilities',
] as const

export function useBroadcastCapabilities() {
  return useQuery<BroadcastCapabilitiesResponse>({
    queryKey: broadcastCapabilitiesKey,
    queryFn: () =>
      ownerApi.get<BroadcastCapabilitiesResponse>(
        '/tenant/broadcast-capabilities',
      ),
    staleTime: 5 * 60 * 1000,
  })
}

export function useCancelBroadcast() {
  const qc = useQueryClient()
  return useMutation<CancelBroadcastResponse, Error, string>({
    mutationFn: (id) =>
      ownerApi.del<CancelBroadcastResponse>(`/broadcasts/${id}`),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: broadcastsKey })
      void qc.invalidateQueries({ queryKey: broadcastDetailKey(id) })
      // A cancelled broadcast never went out — re-read the cadence so the
      // count doesn't include it.
      void qc.invalidateQueries({ queryKey: broadcastCapKey })
    },
  })
}

export function useRemoveBroadcast() {
  const qc = useQueryClient()
  return useMutation<RemoveBroadcastResponse, Error, string>({
    mutationFn: (id) =>
      ownerApi.post<RemoveBroadcastResponse>(`/broadcasts/${id}/remove`),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: broadcastsKey })
      void qc.invalidateQueries({ queryKey: broadcastDetailKey(id) })
      // A removed broadcast still counts toward cadence (it was delivered) —
      // invalidate so the card reflects the move to 'removing'/'removed'.
      void qc.invalidateQueries({ queryKey: broadcastCapKey })
    },
  })
}
